import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "./firebase"; 
import { doc, onSnapshot, query, orderBy, collection, updateDoc, arrayUnion, arrayRemove, increment } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import VinylPlayer from "./VinylPlayer"; 
import "./styles.css";
import ShareModal from './ShareModal';
import { AnalyticsEngine, AnalyticsDashboard, getGlobalStats } from "./services/AnalyticsEngine";

const shuffleArray = (array) => {
  let shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export default function PublicPlayer() {
  const [tracks, setTracks] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const audioRef = useRef(null);
  const playTimeCounterRef = useRef(0); 


  useEffect(() => {
    let interval;
  
    if (isPlaying && tracks[currentTrackIndex]?.id) {
      interval = setInterval(() => {
        playTimeCounterRef.current += 1;
  
        // Alle 30 Sekunden Daten senden
        if (playTimeCounterRef.current >= 30) {
          const trackId = tracks[currentTrackIndex].id;
          
          // Funktion zum Hochzählen in Firebase
          const trackRef = doc(db, "tracks", trackId);
          updateDoc(trackRef, {
            totalPlaytimeSec: increment(30),
          }).catch(err => console.error("Stats Update Error:", err));
  
          playTimeCounterRef.current = 0; // Zähler zurücksetzen
        }
      }, 1000);
    }
  
    return () => clearInterval(interval); // Aufräumen, wenn Pause oder Songwechsel
  }, [isPlaying, currentTrackIndex, tracks]);

  const handleArtistEntry = () => {
    window.location.href = "/station?invite=true";
  };

  const handleTitleClick = () => {
    const newCount = clickCount + 1;
    if (newCount >= 5) {
      handleArtistEntry();
    } else {
      setClickCount(newCount);
      setTimeout(() => setClickCount(0), 2000);
    }
  };

  const [publicId] = useState(() => {
    let id = localStorage.getItem("public_user_id");
    if (!id) {
      id = "pub_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("public_user_id", id);
    }
    return id;
  });


  useEffect(() => {
    const q = query(collection(db, "tracks"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, async (snap) => {
      const allTracks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const publicTracks = allTracks.filter(t => (t.likes?.length > 0) || (t.publicRating > 0));
      
      const repairedTracks = await Promise.all(publicTracks.map(async (track) => {
        if (track.url && track.url.includes("firebasestorage.googleapis.com") && !track.url.includes("token=")) {
          try {
            const pathPart = track.url.split('/o/')[1].split('?')[0];
            const decodedPath = decodeURIComponent(pathPart);
            const newUrl = await getDownloadURL(ref(storage, decodedPath));
            return { ...track, url: newUrl };
          } catch (e) { return track; }
        }
        return track;
      }));

      setTracks(prevTracks => {
        if (prevTracks.length === 0) {
          return repairedTracks.sort(() => Math.random() - 0.5);
        } else {
          return prevTracks.map(oldTrack => {
            const updated = repairedTracks.find(t => t.id === oldTrack.id);
            return updated ? updated : oldTrack;
          });
        }
      });

      const urlParams = new URLSearchParams(window.location.search);
      const trackIdFromUrl = urlParams.get('track');
      if (trackIdFromUrl && repairedTracks.length > 0) {
        const foundIndex = repairedTracks.findIndex(t => t.id === trackIdFromUrl);
        if (foundIndex !== -1) {
          setCurrentTrackIndex(foundIndex);
        }
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (isPlaying && audioRef.current && tracks[currentTrackIndex]?.url) {
      // Wichtig: Wenn der Index wechselt, muss das Audio-Element die neue URL laden
      audioRef.current.load(); 
      audioRef.current.play().catch(e => {
        console.error("Autoplay blockiert:", e);
        setIsPlaying(false); 
      });
    }
  }, [currentTrackIndex, isPlaying]); 

  const currentTrack = tracks[currentTrackIndex] || { title: "LOADING...", artist: "STREETWISE", url: "", likes: [], plays: 0 };
  const [shareData, setShareData] = useState({ visible: false, url: "", text: "" });

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack || !currentTrack.url) return;

    if ('mediaSession' in navigator) {
      // 1. Metadaten setzen
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack.title || "Streetwise Radio",
        artist: currentTrack.artist || "Streetwise",
        artwork: [{ src: '/favicon.png', sizes: '512x512', type: 'image/png' }]
      });

      // 2. Action Handler verknüpfen
      const actions = [
        ['play', togglePlay],
        ['pause', togglePlay],
        ['nexttrack', () => playTrack(currentTrackIndex + 1)],
        ['previoustrack', () => playTrack(currentTrackIndex - 1)]
      ];

      actions.forEach(([action, handler]) => {
        try {
          navigator.mediaSession.setActionHandler(action, handler);
        } catch (e) { console.log(action + " not supported"); }
      });
    }

    // 3. Standby-Synchronisation (Position & Dauer)
    const updatePos = () => {
      if ('setPositionState' in navigator.mediaSession && audio.duration && !isNaN(audio.duration)) {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          playbackRate: audio.playbackRate,
          position: audio.currentTime
        });
      }
    };

    audio.addEventListener('play', updatePos);
    audio.addEventListener('pause', updatePos);
    audio.addEventListener('timeupdate', updatePos);

    return () => {
      audio.removeEventListener('play', updatePos);
      audio.removeEventListener('pause', updatePos);
      audio.removeEventListener('timeupdate', updatePos);
    };
  }, [currentTrack, currentTrackIndex]);


  const toggleLike = async (e, trackId, likesArray = []) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const trackRef = doc(db, "tracks", trackId);
    const safeLikes = Array.isArray(likesArray) ? likesArray : [];
    try {
      if (safeLikes.includes(publicId)) {
        await updateDoc(trackRef, { likes: arrayRemove(publicId) });
      } else {
        await updateDoc(trackRef, { likes: arrayUnion(publicId) });
      }
    } catch (err) { console.error("Like Error:", err); }
  };

  const playTrack = (i) => {
    if (tracks.length === 0) return;
    let nextIdx = i;
  
    if (nextIdx >= tracks.length) {
      const freshShuffle = shuffleArray(tracks);
      setTracks(freshShuffle);
      nextIdx = 0;
    } else if (nextIdx < 0) {
      nextIdx = tracks.length - 1;
    }
  
    // Stats Update bleibt gleich...
    const selectedTrack = tracks[nextIdx];
    if (selectedTrack && selectedTrack.id) {
      const trackRef = doc(db, "tracks", selectedTrack.id);
      updateDoc(trackRef, { plays: increment(1) }).catch(err => console.error(err));
    }
  
    setCurrentTrackIndex(nextIdx);
    setIsPlaying(true);
    
    // WICHTIG: Wir nutzen useEffect, um das Playback zu triggern, 
    // aber wir können hier einen kleinen "Force-Play" machen.
    if (audioRef.current) {
      audioRef.current.pause(); // Erst stoppen
      // Wir setzen die src manuell, falls currentTrack sich nicht schnell genug updatet
      audioRef.current.src = tracks[nextIdx].url; 
      audioRef.current.load();
      audioRef.current.play().catch(e => console.log("Autoplay blockiert:", e));
    }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().catch(e => console.log(e));
    } else {
      audioRef.current.pause();
    }
  };

  const setAudioTime = (time) => {
    if (audioRef.current) audioRef.current.currentTime = time;
  };

  const handleShare = async (type) => {
    const publicUrl = "https://radio.streetwise.studio";
    const shareUrl = type === 'app' ? publicUrl : `${publicUrl}?track=${currentTrack.id}`;
    
    // Der Text, der in WhatsApp/Telegram erscheinen soll
    const shareText = type === 'app' 
      ? "Hey, kennst Du schon unser neues streetwise RADIO? Hier kannst du mal reinhören: " 
      : `Hey, der Track "${currentTrack.title}" ist jetzt LIVE ON AIR im streetwise RADIO`;
  
    if (navigator.share) {
      try {
        await navigator.share({ title: "streetwise RADIO", text: shareText, url: shareUrl });
      } catch (err) { console.log("Share failed"); }
    } else {
      // Hier übergeben wir jetzt "text" passend zum State
      setShareData({ visible: true, url: shareUrl, text: shareText });
    }
  };

  

  const [deferredPrompt, setDeferredPrompt] = useState(null);

    useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleFastInstall = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
    if (isIOS) {
      alert("Installation auf iOS:\n\n1. Tippe unten auf das Teilen-Icon [⎋]\n2. Wähle 'Zum Home-Bildschirm hinzufügen'.");
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setDeferredPrompt(null);
    } else {
      // Fallback für Browser, die den Prompt noch nicht bereit haben
      alert("Tippe auf die drei Punkte [⋮] im Browser-Menü und wähle 'App installieren' oder 'Zum Startbildschirm hinzufügen'.");
    }
  };


  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#000", color: "#fff", fontFamily: "sans-serif" }}>
      <audio 
        ref={audioRef} 
        src={currentTrack.url}
        playsInline // WICHTIG: Füge das hinzu
        onPlay={() => {
          setIsPlaying(true);
          if ('mediaSession' in navigator) navigator.mediaSession.playbackState = "playing";
        }}
        preload="auto"
        onPause={() => {
          setIsPlaying(false);
          if ('mediaSession' in navigator) navigator.mediaSession.playbackState = "paused";
        }}
        onTimeUpdate={() => setProgress(audioRef.current.currentTime)} 
        onLoadedMetadata={() => setDuration(audioRef.current.duration)} 
        onEnded={() => playTrack(currentTrackIndex + 1)} // SOFORT weiter, kein setTimeout!
      />

      <header style={{ padding: "1.5rem", textAlign: "center", borderBottom: "1px solid #111" }}>
        <h1 
          onClick={handleTitleClick}
          style={{ fontSize: "0.8rem", color: "#00acc1", letterSpacing: "5px", fontWeight: "900", cursor: "pointer", userSelect: "none" }}
        >
          STREETWISE RADIO
        </h1>
        <div style={{ fontSize: "0.55rem", color: "#555", marginTop: "5px", letterSpacing: "2px" }}>PUBLIC SELECTION</div>
      </header>


      <main style={{ padding: "1.5rem", maxWidth: "600px", margin: "0 auto", textAlign: "center" }}>
        
        {/* 1. Vinyl Player (Buttons oben sind entfernt) */}
        <VinylPlayer isPlaying={isPlaying} currentTrack={currentTrack} />

        {/* 2. Fortschrittsbalken */}
        <div style={{ marginBottom: "15px", marginTop: "10px" }}>
          <input 
            type="range" min="0" max={duration || 0} value={progress} 
            onChange={e => setAudioTime(e.target.value)} 
            style={{ width: "100%", accentColor: "#00acc1" }} 
          />
        </div>

        {/* 3. Player Steuerung */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginBottom: "10px", height: "80px" }}>
          <div style={{ width: "50px" }}></div>

          <div style={{ display: "flex", gap: "25px", alignItems: "center" }}>
            <button onClick={() => playTrack(currentTrackIndex - 1)} style={{ background: "none", border: "none", color: "#fff", fontSize: "1.5rem", cursor: "pointer" }}>⏮</button>
            <button onClick={togglePlay} style={{ width: "70px", height: "70px", borderRadius: "50%", background: "#fff", color: "#000", border: "none", fontSize: "1.5rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {isPlaying ? "⏸" : "▶"}
            </button>
            <button onClick={() => playTrack(currentTrackIndex + 1)} style={{ background: "none", border: "none", color: "#fff", fontSize: "1.5rem", cursor: "pointer" }}>⏭</button>
          </div>

          <div style={{ width: "50px", textAlign: "right", position: "relative", zIndex: 100 }}>
            <button 
              onMouseDown={(e) => e.stopPropagation()} 
              onClick={(e) => toggleLike(e, currentTrack.id, currentTrack.likes)} 
              style={{ background: "none", border: "none", cursor: "pointer", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}
            >
              <span style={{ fontSize: "1.8rem", pointerEvents: "none" }}>
                {currentTrack.likes?.includes(publicId) ? "👍" : "🖐️"}
              </span>
              <span style={{ fontSize: "0.6rem", color: "#00acc1", fontWeight: "bold", pointerEvents: "none" }}>
                {currentTrack.likes?.length || 0}
              </span>
            </button>
          </div>
        </div>

        {/* 4. SHARE BUTTONS */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", marginBottom: "30px" }}>
          <button onClick={() => handleShare('app')} className="btn-station-cyan">SHARE APP 🔗</button>
          <button onClick={() => handleShare('track')} className="btn-station-cyan">SHARE TRACK 🎵</button>
        </div>

        {/* 5. Playlist */}
        <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: "20px", border: "1px solid #222", marginBottom: "40px" }}>
          <button onClick={() => setIsArchiveOpen(!isArchiveOpen)} style={{ width: "100%", padding: "15px", background: "none", border: "none", color: "#00acc1", fontSize: "0.7rem", fontWeight: "bold", display: "flex", justifyContent: "space-between", cursor: "pointer" }}>
            <span>OPEN PLAYLIST ({tracks.length})</span>
            <span>{isArchiveOpen ? "▲" : "▼"}</span>
          </button>
          {isArchiveOpen && (
            <div style={{ padding: "10px", maxHeight: "300px", overflowY: "auto" }}>
              {tracks.map((t, idx) => (
                <div key={t.id} onClick={() => playTrack(idx)} style={{ display: "flex", justifyContent: "space-between", padding: "12px", background: currentTrackIndex === idx ? "rgba(0,172,193,0.1)" : "none", borderRadius: "8px", cursor: "pointer", marginBottom: "5px" }}>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.85rem", color: currentTrackIndex === idx ? "#00acc1" : "#fff" }}>{t.title}</div>
                    <div style={{ fontSize: "0.65rem", color: "#888" }}>{t.artist}</div>
                  </div>
                  <span style={{ color: "#00acc1", fontSize: "0.8rem" }}>♥ {t.likes?.length || 0}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Die schlaue Analytics Engine übernimmt jetzt Trigger & Anzeige intern */}
        <AnalyticsDashboard 
          tracks={tracks} 
          currentTrackIndex={currentTrackIndex} 
          isSecret={true} 
        />


        {/* SUBTILER INSTALL BUTTON (Verschoben aus App.js Logik) */}
        {!window.matchMedia('(display-mode: standalone)').matches && (
          <button 
            onClick={handleFastInstall} 
            className="btn-station-grey"            
            style={{ display: "block", margin: "40px auto", padding: "8px 16px" }}
          >
            INSTALL APP
          </button>
        )}

        <div style={{ padding: "40px 0", borderTop: "1px solid #111" }}>
          <button 
            onClick={handleArtistEntry}
            className="btn-station-cyan"
          >
            <span style={{ marginRight: "8px" }}>↑</span>
            UPLOAD YOUR OWN MUSIC
          </button>
          <p style={{ color: "#444", fontSize: "0.5rem", marginTop: "10px" }}>ARTIST ACCESS ONLY</p>
        </div>

        <ShareModal 
          isOpen={shareData.visible} 
          onClose={() => setShareData({ ...shareData, visible: false })}
          url={shareData.url}
          text={shareData.text}
        />
      </main>

    </div>
  );
}